import { TypeInitProps, TypeRequest } from '../../../types';
import { encryptParams, objectToString } from '../../../utils/index';
import { WEB_AFF_URL } from '../../../utils/urls';
import { get } from '../../../utils/data';
export default class LoginAPI {
  private request: any;
  constructor(props: TypeInitProps & { request: TypeRequest }) {
    const { request } = props;
    this.request = request;
  }
  /**
   * @param {string} mobile - the mobile used to login
   * @return {Promise} send result
   */
  sendCode = (params: { mobile: string; extra_params?: object }) => {
    const { mobile, extra_params = {} } = params;
    const data = encryptParams({ mobile, type: 7, ...extra_params }, [
      'mobile',
      'type',
    ]);
    return this.request({
      url: WEB_AFF_URL.SEND_MOBILE_CODE,
      method: 'POST',
      data,
    });
  };

  /**
   * @param {string} mobile - the mobile used to login
   * @param {string} code - the captcha
   * @param {number} need_frozen_user - whether return users in frozen
   * @return {Promise} login result
   */
  smsLogin = (params: {
    mobile: string;
    code: string;
    need_frozen_user?: number;
    extra_params?: object;
  }) => {
    const { mobile, code, need_frozen_user = 0, extra_params = {} } = params;
    const data = encryptParams(
      { mobile, code, need_frozen_user, type: 7, ...extra_params },
      ['mobile', 'type', 'code']
    );
    return this.request({
      url: WEB_AFF_URL.SMS_LOGIN,
      method: 'POST',
      data,
    });
  };

  /**
   * @param {string} email - the email used to login
   * @return {Promise} send result
   */
  sendEmailCode = (params: { email: string; extra_params?: object }) => {
    const { email, extra_params = {} } = params;
    const data = encryptParams({ email, type: 13, ...extra_params }, [
      'email',
      'type',
    ]);
    return this.request({
      url: WEB_AFF_URL.SEND_EMAIL_CODE,
      method: 'POST',
      data,
    });
  };

  /**
   * @param {string} email - the email used to login
   * @param {string} code - the captcha
   * @param {number} need_frozen_user - whether return users in frozen
   * @return {Promise} login result
   */
  emailLogin = (params: {
    email: string;
    code: string;
    need_frozen_user?: number;
    extra_params?: object;
  }) => {
    const { email, code, need_frozen_user = 0, extra_params = {} } = params;
    const data = encryptParams(
      { email, code, need_frozen_user, type: 13, ...extra_params },
      ['email', 'type', 'code']
    );
    return this.request({
      url: WEB_AFF_URL.EMAIL_LOGIN,
      method: 'POST',
      data,
    });
  };

  /**
   * @param {string} email - the email used to login
   * @param {string} username - the username used to login
   * @param {string} mobile - the mobile used to login
   * @param {string} account - the account used to login, whose value shoule be email or username or mobile
   * @param {string} password - corresponding password
   * @param {number} need_frozen_user - whether return users in frozen
   * @return {Promise} login result
   */
  pwdLogin = (params: {
    [key: string]: any;
    [index: number]: any;
    email?: string;
    username?: string;
    mobile?: string;
    account?: string;
    password: string;
    need_frozen_user?: number;
    extra_params?: object;
  }) => {
    const { password, need_frozen_user = 0, extra_params = {} } = params;
    const loginType = ['email', 'username', 'mobile', 'account'].find(
      item => !!params[item]
    );
    if (!loginType) {
      return Promise.reject();
    }
    let data = encryptParams(
      {
        password,
        [loginType]: params[loginType],
        need_frozen_user,
        ...extra_params,
      },
      ['password', loginType]
    );
    return this.request({
      url: WEB_AFF_URL.PWD_LOGIN,
      method: 'POST',
      data,
    });
  };

  /**
   * @param {number} platform_app_id - the app id of the third party used to login
   * @param {string} next - encoded redirect url after successful authorization
   * @return {Promise} authorize result
   */
  oauthLoginCall = (params: {
    platform_app_id: number;
    next: string;
    extra_params?: object;
  }) => {
    const { platform_app_id, next, extra_params = {} } = params;
    const { aid, subjectAid } = get('initProps') || {};
    const queryStr = objectToString({
      platform_app_id,
      next,
      aid,
      subject_aid: subjectAid,
      account_sdk_source: 'web',
      ...extra_params,
    });
    window.location.href = `${WEB_AFF_URL.OAUTH_LOGIN_CALL}?${queryStr}`;
    // return new Promise((resolve, reject) => {
    //   this.request({
    //     url: WEB_AFF_URL.OAUTH_LOGIN_CALL,
    //     method: 'GET',
    //     data: {
    //       platform_app_id,
    //       next,
    //     },
    //   })
    //     .then((data: any) => {
    //       resolve(data);
    //     })
    //     .catch((e: any) => {
    //       reject(e);
    //     });
    // });
  };

  /**
   * @param {string} platform_app_id - the app id of the third party used to login
   * @param {string} redirect_uri - parameter 'oauth_redirect_uri' in the url query after calling oauthLoginCall successfully
   * @param {string} code - parameter 'oauth_code' in the url query after calling oauthLoginCall successfully
   * @param {number} need_frozen_user - whether return users in frozen
   * @param {string} login_only - 'true' for only login, 'false' for register and login
   * @return {Promise} login result
   */
  oauthLogin = (params: {
    platform_app_id: number;
    redirect_uri: string;
    code: string;
    need_frozen_user?: number;
    login_only?: boolean;
    extra_params?: object;
  }) => {
    const {
      platform_app_id,
      redirect_uri,
      code,
      need_frozen_user = 0,
      login_only = true,
      extra_params = {},
    } = params;
    return this.request({
      url: login_only ? WEB_AFF_URL.OAUTH_LOGIN_ONLY : WEB_AFF_URL.OAUTH_LOGIN,
      method: 'POST',
      data: {
        platform_app_id,
        redirect_uri,
        code,
        need_frozen_user,
        ...extra_params,
      },
    });
  };

  /**
   * @return {Promise} list of companies the user can login
   */
  subjectLoginList = (params: { extra_params?: object } = {}) => {
    const { extra_params = {} } = params;
    return this.request({
      url: WEB_AFF_URL.SUBJECT_LOGIN_LIST,
      method: 'GET',
      data: extra_params,
    });
  };

  /**
   * @param {string} login_subject_uid - the id of the subject user want to login
   * @param {string} user_identity_id - the id of the user who wants to login
   * @return {Promise} login result
   */
  subjectLogin = (params: {
    login_subject_uid: string;
    user_identity_id: string;
    extra_params?: object;
  }) => {
    const {
      login_subject_uid,
      user_identity_id = '0',
      extra_params = {},
    } = params;
    const data = encryptParams(
      { login_subject_uid, user_identity_id, ...extra_params },
      ['login_subject_uid', 'user_identity_id']
    );
    return this.request({
      url: WEB_AFF_URL.SUBJECT_LOGIN,
      method: 'POST',
      data,
    });
  };

  /**
   * @param {string} next - the redirect url after logout successfully
   * @return {Promise} logout result
   */
  logout = (params: { next: string; not_redirect?: boolean }) => {
    const { next = '', not_redirect = false } = params;
    const { aid, subjectAid } = get('initProps') || {};
    const data = {
      account_sdk_source: 'web',
      aid,
      subject_aid: subjectAid,
      next,
    };
    if (not_redirect) {
      return this.request({
        url: WEB_AFF_URL.LOGOUT,
        method: 'GET',
        data: {
          ...data,
          next: encodeURIComponent(data.next),
        },
      });
    }
    window.location.href = `${WEB_AFF_URL.LOGOUT}?${objectToString(data)}`;
  };

  /**
   * [Industry account user switching web]
   * @param {string} Industry account system userIdentityId
   */
  memberSwitch = (params: {
    to_sec_member_id: string;
    extra_params?: object;
  }) => {
    let { to_sec_member_id, extra_params = {} } = params;
    return this.request({
      url: WEB_AFF_URL.MEMBER_SWITCH,
      method: 'POST',
      data: {
        to_sec_member_id,
        ...extra_params,
      },
    });
  };
}
